/**
 * TABLE HEADER COMPONENT
 * Auto-sized, intelligent header with dynamic column management
 * @version 1.0.0
 * @production-ready
 */

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { ColumnDefinition, TableStyleConfig } from '../types';
import { calculateHeaderFontSize } from '../utils';

interface TableHeaderProps {
  columns: ColumnDefinition[];
  columnWidths: number[];
  styleConfig: TableStyleConfig;
  rowKeys?: string[];
  onRowKeyChange?: (index: number, newKey: string) => void;
  onRemoveRowKey?: (index: number) => void;
  topRowContent?: React.ReactNode;
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  columns,
  columnWidths,
  styleConfig,
  rowKeys = [],
  onRowKeyChange,
  onRemoveRowKey,
  topRowContent
}) => {
  const condensed = styleConfig.condensed ?? true;
  const headerHeight = styleConfig.headerHeight ?? (condensed ? 32 : 44);
  const unitRowHeight = styleConfig.unitRowHeight ?? (condensed ? 22 : 26);
  const baseHeaderFontSize = styleConfig.headerFontSize ?? (condensed ? 10 : 13);
  const unitFontSize = styleConfig.unitFontSize ?? (condensed ? 11 : 12);
  const hasUnitLabels = columns.some((column) => !!column.unitLabel);

  /**
   * Force column header text (spans) to stay bold even when global span rules override
   */
  useEffect(() => {
    const headerSpans = document.querySelectorAll('.entry-page .entry-table-header-span, .entry-page thead th span');
    headerSpans.forEach((el) => {
      const span = el as HTMLElement;
      span.style.setProperty('font-weight', '700', 'important');
      span.style.setProperty('font-family', styleConfig.fontFamily || 'IBM Plex Sans, sans-serif', 'important');
      span.style.setProperty('color', 'var(--color-primary-blue)', 'important');
    });
  }, [columns, columnWidths, rowKeys, styleConfig.fontFamily]);

  /**
   * Render static column header (from column definitions)
   */
  const renderStaticColumn = (column: ColumnDefinition, index: number) => {
    const width = columnWidths[index];
    const label = condensed && column.abbreviation ? column.abbreviation : column.label;
    const fontSize = calculateHeaderFontSize(label.length, width, baseHeaderFontSize);
    const isSticky = column.sticky ?? false;
    const stickyLeft = (column as any).stickyLeft || 0;

    const cellStyle: React.CSSProperties = {
      width: `${width}px`,
      minWidth: `${width}px`,
      maxWidth: `${width}px`,
      height: `${headerHeight}px`,
      fontSize: `${fontSize}px`,
      background: column.headerColor || 'var(--color-gray-200)',
      color: 'var(--color-primary-blue)',
      fontWeight: 700,
      textAlign: column.textAlign || 'center',
      padding: '3px 4px',
      verticalAlign: 'middle',
      position: isSticky ? 'sticky' : 'relative',
      left: isSticky ? `${stickyLeft}px` : undefined,
      zIndex: isSticky ? 30 : 20,
      borderRight: isSticky ? '2px solid var(--color-gray-400)' : undefined,
      boxShadow: isSticky ? 'var(--shadow-sm)' : undefined,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontFamily: styleConfig.fontFamily
    };

    const isFirstCell = index === 0;

    if (isFirstCell && topRowContent) {
      cellStyle.overflow = 'visible';
      cellStyle.padding = '0';
      cellStyle.cursor = 'pointer';
    }

    return (
      <th
        key={column.key}
        style={cellStyle}
        className="border entry-table-header cursor-pointer"
        title={column.description || column.label}
        data-sticky={isSticky}
        data-column-key={column.key}
      >
        {isFirstCell && topRowContent ? (
          <div className="flex items-center justify-center w-full h-full cursor-pointer" style={{ pointerEvents: 'auto', zIndex: 50, cursor: 'pointer' }}>
            {topRowContent}
          </div>
        ) : (
          <span 
            className="entry-table-header-span"
            style={{ color: 'var(--color-primary-blue)', fontWeight: 700, display: 'block', width: '100%' }}
          >
            {label}
          </span>
        )}
      </th>
    );
  };

  /**
   * Render dynamic column header (for date columns, etc.)
   */
  const renderDynamicColumn = (rowKey: string, index: number) => {
    const width = 120; // Default for dynamic columns
    const fontSize = baseHeaderFontSize;

    return (
      <th
        key={`dynamic-${index}`}
        className="border"
        style={{
          width: `${width}px`,
          minWidth: `${width}px`,
          maxWidth: `${width}px`,
          height: `${headerHeight}px`,
          fontSize: `${fontSize}px`,
          background: 'var(--color-gray-200)',
          color: '#000000',
          fontWeight: 700,
          padding: '2px',
          verticalAlign: 'middle',
          textAlign: 'center',
          fontFamily: styleConfig.fontFamily
        }}
      >
        {onRowKeyChange && onRemoveRowKey ? (
          // Editable date header with remove button
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '2px'
            }}
          >
            <input
              type="date"
              value={rowKey.includes('_') ? rowKey.split('_')[0] : rowKey}
              onChange={(e) => onRowKeyChange(index, e.target.value)}
              style={{
                fontSize: `${fontSize + 1}px`,
                color: 'var(--color-primary-blue)',
                fontWeight: 400,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                textAlign: 'center',
                width: '90%',
                cursor: 'pointer',
                fontFamily: styleConfig.fontFamily
              }}
              title="Click to change date"
            />
            <button
              onClick={() => onRemoveRowKey(index)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '1px',
                color: 'var(--color-status-error)',
                transition: 'var(--transition-fast)',
                borderRadius: 'var(--radius-sm)'
              }}
              className="hover:bg-red-100"
              title="Remove this date"
            >
              <X size={condensed ? 8 : 10} />
            </button>
          </div>
        ) : (
          // Static display of row key - extract date part if composite key
          <span title={rowKey} style={{ color: '#000000', fontWeight: 700 }}>
            {rowKey.includes('_') ? rowKey.split('_')[0] : rowKey}
          </span>
        )}
      </th>
    );
  };

  return (
    <thead style={{ position: 'sticky', top: 0, zIndex: 20, background: 'var(--color-gray-200)' }}>
      <tr style={{ height: `${headerHeight}px` }}>
        {/* Render static columns from definitions */}
        {columns.map(renderStaticColumn)}

        {/* Render dynamic columns (dates, etc.) */}
        {rowKeys.map(renderDynamicColumn)}
      </tr>
      {hasUnitLabels && (
        <tr style={{ height: `${unitRowHeight}px`, background: 'var(--color-gray-100)' }}>
          {columns.map((column, index) => {
            const width = columnWidths[index];
            const isSticky = column.sticky ?? false;
            const stickyLeft = (column as any).stickyLeft || 0;
            const unitLabel = column.unitLabel || '\u00A0';

            return (
              <th
                key={`${column.key}-unit`}
                className="border entry-table-subheader"
                style={{
                  width: `${width}px`,
                  minWidth: `${width}px`,
                  maxWidth: `${width}px`,
                  height: `${unitRowHeight}px`,
                  fontSize: `${unitFontSize}px`,
                  background: 'var(--color-gray-100)',
                  color: 'var(--color-gray-700)',
                  fontWeight: 700,
                  textAlign: column.textAlign || 'center',
                  padding: '2px 4px',
                  verticalAlign: 'middle',
                  position: isSticky ? 'sticky' : 'relative',
                  left: isSticky ? `${stickyLeft}px` : undefined,
                  zIndex: isSticky ? 25 : 15,
                  borderRight: isSticky ? '2px solid var(--color-gray-300)' : undefined,
                  boxShadow: isSticky ? 'var(--shadow-sm)' : undefined,
                  fontFamily: styleConfig.fontFamily
                }}
              >
                <span
                  style={{
                    display: 'block',
                    width: '100%',
                    color: 'var(--color-gray-700)',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {unitLabel}
                </span>
              </th>
            );
          })}
          {/* Unit cells for dynamic columns (dates) */}
          {rowKeys.map((rowKey, index) => {
            const width = 120; // Default for dynamic columns
            return (
              <th
                key={`dynamic-${index}-unit`}
                className="border entry-table-subheader"
                style={{
                  width: `${width}px`,
                  minWidth: `${width}px`,
                  maxWidth: `${width}px`,
                  height: `${unitRowHeight}px`,
                  fontSize: `${unitFontSize}px`,
                  background: 'var(--color-gray-100)',
                  color: 'var(--color-gray-700)',
                  fontWeight: 700,
                  textAlign: 'center',
                  padding: '2px 4px',
                  verticalAlign: 'middle',
                  fontFamily: styleConfig.fontFamily
                }}
              >
                <span
                  style={{
                    display: 'block',
                    width: '100%',
                    color: 'var(--color-gray-700)',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {'\u00A0'}
                </span>
              </th>
            );
          })}
        </tr>
      )}
    </thead>
  );
};
